import type { LimitOrderIdentity, LimitOrderResponse } from '@/utils/0x.types';
import { getLimitOrderIdentityKey } from '@/utils/limit-order-identity';

const OPEN_LIMIT_ORDER_TOMBSTONES_STORAGE_KEY = 'matcha-open-limit-order-tombstones';
export const OPEN_LIMIT_ORDER_TOMBSTONE_TTL_MS = 10 * 60_000;

type Tombstones = Record<string, number>;

const canUseStorage = (): boolean => typeof window !== 'undefined' && 'localStorage' in window;

const pruneExpiredTombstones = (tombstones: Tombstones, now: number): Tombstones =>
  Object.fromEntries(Object.entries(tombstones).filter(([, expiresAt]) => expiresAt > now));

const persistTombstones = (tombstones: Tombstones): void => {
  if (!canUseStorage()) return;

  if (Object.keys(tombstones).length === 0) {
    window.localStorage.removeItem(OPEN_LIMIT_ORDER_TOMBSTONES_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(OPEN_LIMIT_ORDER_TOMBSTONES_STORAGE_KEY, JSON.stringify(tombstones));
};

const readTombstones = (now: number = Date.now()): Tombstones => {
  if (!canUseStorage()) return {};

  try {
    const storedValue = window.localStorage.getItem(OPEN_LIMIT_ORDER_TOMBSTONES_STORAGE_KEY);
    if (!storedValue) return {};

    const parsed = JSON.parse(storedValue) as Tombstones;
    // Tombstones written before provider-scoped keys used the bare 1inch
    // order hash; rewrite them so recent cancellations stay hidden.
    const hasLegacyKeys = Object.keys(parsed).some((key) => !key.includes(':'));
    const migrated = hasLegacyKeys
      ? Object.fromEntries(
          Object.entries(parsed).map(([key, expiresAt]) => [
            key.includes(':')
              ? key
              : getLimitOrderIdentityKey({ provider: '1inch', orderHash: key }),
            expiresAt,
          ]),
        )
      : parsed;
    const pruned = pruneExpiredTombstones(migrated, now);

    if (hasLegacyKeys || Object.keys(pruned).length !== Object.keys(parsed).length) {
      persistTombstones(pruned);
    }

    return pruned;
  } catch {
    window.localStorage.removeItem(OPEN_LIMIT_ORDER_TOMBSTONES_STORAGE_KEY);
    return {};
  }
};

export const addOpenLimitOrderTombstones = (
  identities: LimitOrderIdentity[],
  now: number = Date.now(),
): void => {
  if (identities.length === 0) return;

  const tombstones = readTombstones(now);
  identities.forEach((identity) => {
    tombstones[getLimitOrderIdentityKey(identity)] = now + OPEN_LIMIT_ORDER_TOMBSTONE_TTL_MS;
  });
  persistTombstones(tombstones);
};

export const filterOpenLimitOrdersWithTombstones = <T extends LimitOrderIdentity>(
  orders: T[],
  now: number = Date.now(),
): T[] => {
  const tombstones = readTombstones(now);
  if (Object.keys(tombstones).length === 0) return orders;

  return orders.filter((order) => !tombstones[getLimitOrderIdentityKey(order)]);
};

export const applyOpenLimitOrderTombstones = (
  response: LimitOrderResponse,
  now: number = Date.now(),
): LimitOrderResponse => {
  const records = filterOpenLimitOrdersWithTombstones(response.records, now);
  const hiddenCount = response.records.length - records.length;

  if (hiddenCount === 0) return response;

  return {
    ...response,
    records,
    total: Math.max(0, response.total - hiddenCount),
  };
};
